import React, { useEffect, useState } from 'react';
import { fileDispute, getMyFiledDisputes } from '../services/api';
import { REASON_LABELS, reasonLabel } from '../constants/disputes';

interface DisputeFormProps {
  quoteRequestId: number;
}

type DisputeStatus = 'pending' | 'upheld' | 'dismissed';

// The subset of /filed-by-me fields this surface consumes. admin_notes is only
// populated once an admin resolves the dispute.
interface FiledDispute {
  dispute_id: number;
  quote_request_id: number;
  reason_category: string;
  description: string;
  status: DisputeStatus;
  created_at: string;
  admin_notes?: string | null;
}

// View phases:
//   'loading'      — fetching existing disputes; render nothing
//   'fetch-error'  — pre-check failed; fall back to trigger button (no pre-check)
//   'closed'       — trigger button visible
//   'open'         — form expanded (submit errors surface inline here)
//   'submitting'   — form disabled, spinner on submit
//   'terminal'     — read-only panel for an existing/just-filed dispute
type Phase = 'loading' | 'fetch-error' | 'closed' | 'open' | 'submitting' | 'terminal';

const MIN_DESC = 10;
const MAX_DESC = 2000;

const REASON_KEYS = Object.keys(REASON_LABELS) as Array<
  'no_show' | 'poor_work' | 'safety' | 'other'
>;

const formatFiledAt = (iso: string): string => {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const statusPill = (status: DisputeStatus): { label: string; style: React.CSSProperties } => {
  if (status === 'upheld') return { label: 'Upheld', style: styles.pillUpheld };
  if (status === 'dismissed') return { label: 'Dismissed', style: styles.pillDismissed };
  return { label: 'Under review', style: styles.pillPending };
};

const panelAccent = (status: DisputeStatus): React.CSSProperties => {
  if (status === 'upheld') return { borderLeft: '4px solid #dc2626' };
  if (status === 'dismissed') return { borderLeft: '4px solid #cbd5e1' };
  return { borderLeft: '4px solid #f59e0b' };
};

const extractList = (data: any): FiledDispute[] => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.disputes)) return data.disputes;
  return [];
};

const DisputeForm: React.FC<DisputeFormProps> = ({ quoteRequestId }) => {
  const [phase, setPhase] = useState<Phase>('loading');
  const [dispute, setDispute] = useState<FiledDispute | null>(null);

  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [submitError, setSubmitError] = useState('');

  // On mount: pre-check whether the homeowner already filed against this request.
  useEffect(() => {
    let active = true;
    getMyFiledDisputes()
      .then((res) => {
        if (!active) return;
        const match = extractList(res.data).find((d) => d.quote_request_id === quoteRequestId);
        if (match) {
          setDispute(match);
          setPhase('terminal');
        } else {
          setPhase('closed');
        }
      })
      .catch(() => {
        if (active) setPhase('fetch-error');
      });
    return () => {
      active = false;
    };
  }, [quoteRequestId]);

  const resetForm = () => {
    setReason('');
    setDescription('');
    setSubmitError('');
  };

  const openForm = () => {
    setSubmitError('');
    setPhase('open');
  };

  const cancelForm = () => {
    resetForm();
    setPhase('closed');
  };

  const canSubmit = reason !== '' && description.trim().length >= MIN_DESC;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setPhase('submitting');
    setSubmitError('');
    try {
      const res = await fileDispute({
        quote_request_id: quoteRequestId,
        reason_category: reason as 'no_show' | 'poor_work' | 'safety' | 'other',
        description: description.trim(),
      });
      // Build the terminal record from the form values + the create response.
      setDispute({
        dispute_id: res.data.dispute_id,
        quote_request_id: quoteRequestId,
        reason_category: reason,
        description: description.trim(),
        status: (res.data.status as DisputeStatus) || 'pending',
        created_at: res.data.created_at || new Date().toISOString(),
        admin_notes: null,
      });
      setPhase('terminal');
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 409) {
        // Duplicate race — we already pre-checked, but a dispute exists. Refetch
        // and show whichever one is on record.
        try {
          const refetch = await getMyFiledDisputes();
          const match = extractList(refetch.data).find((d) => d.quote_request_id === quoteRequestId);
          if (match) {
            setDispute(match);
            setPhase('terminal');
            return;
          }
        } catch {
          /* fall through to inline error */
        }
        setSubmitError('You have already filed an issue for this job.');
        setPhase('open');
      } else if (status === 400) {
        setSubmitError(err?.response?.data?.error || `Description must be ${MIN_DESC}–${MAX_DESC} characters.`);
        setPhase('open');
      } else {
        setSubmitError('Something went wrong. Please try again.');
        setPhase('open');
      }
    }
  };

  if (phase === 'loading') return null;

  // Terminal read-only panel (pre-existing or freshly filed).
  if (phase === 'terminal' && dispute) {
    const pill = statusPill(dispute.status);
    const showNotes = dispute.status !== 'pending' && !!dispute.admin_notes;
    return (
      <div style={{ ...styles.terminalPanel, ...panelAccent(dispute.status) }}>
        <span style={{ ...styles.pillBase, ...pill.style }}>{pill.label}</span>
        <div style={styles.terminalFiled}>Dispute filed {formatFiledAt(dispute.created_at)}</div>
        <div style={styles.terminalRow}>
          <span style={styles.terminalLabel}>Reason</span>
          <span style={styles.terminalValue}>{reasonLabel(dispute.reason_category)}</span>
        </div>
        <div style={styles.terminalDescBlock}>
          <div style={styles.terminalLabel}>Your description</div>
          <p style={styles.terminalDesc}>{dispute.description}</p>
        </div>
        {showNotes && (
          <div style={styles.terminalNotesBlock}>
            <div style={styles.terminalLabel}>Notes from review</div>
            <p style={styles.terminalDesc}>{dispute.admin_notes}</p>
          </div>
        )}
      </div>
    );
  }

  // Trigger button (closed, or fetch-error fallback with no pre-check).
  if (phase === 'closed' || phase === 'fetch-error') {
    return (
      <button type="button" style={styles.triggerBtn} onClick={openForm}>
        Report an issue with this contractor
      </button>
    );
  }

  // Expanded form ('open' / 'submitting').
  const submitting = phase === 'submitting';
  return (
    <div style={styles.formPanel}>
      <h3 style={styles.formHeading}>Report an issue with this contractor</h3>

      <div style={styles.fieldLabel}>What went wrong?</div>
      <div style={styles.radioGroup}>
        {REASON_KEYS.map((key) => (
          <label key={key} style={styles.radioRow}>
            <input
              type="radio"
              name="dispute-reason"
              value={key}
              checked={reason === key}
              disabled={submitting}
              onChange={() => setReason(key)}
              style={styles.radioInput}
            />
            <span style={styles.radioText}>{REASON_LABELS[key]}</span>
          </label>
        ))}
      </div>

      <div style={styles.fieldLabel}>Describe what happened</div>
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESC))}
        disabled={submitting}
        rows={4}
        maxLength={MAX_DESC}
        placeholder="Give the details an admin will need to review this issue."
        style={styles.textarea}
      />
      <div style={styles.counter}>
        {description.trim().length < MIN_DESC
          ? `${MIN_DESC - description.trim().length} more character${
              MIN_DESC - description.trim().length === 1 ? '' : 's'
            } needed`
          : `${description.length} / ${MAX_DESC}`}
      </div>

      {submitError && <div style={styles.errorText}>{submitError}</div>}

      <div style={styles.formActions}>
        <button type="button" onClick={cancelForm} disabled={submitting} style={styles.cancelBtn}>
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting || !canSubmit}
          style={{ ...styles.submitBtn, opacity: submitting || !canSubmit ? 0.5 : 1 }}
        >
          {submitting ? 'Submitting…' : 'Submit issue'}
        </button>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  triggerBtn: {
    padding: '10px 18px',
    borderRadius: 8,
    border: '1px solid #cbd5e1',
    background: '#fff',
    color: '#475569',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  formPanel: {
    background: '#fffbeb',
    border: '1px solid #fcd34d',
    borderLeft: '4px solid #f59e0b',
    borderRadius: 12,
    padding: 20,
  },
  formHeading: { margin: '0 0 16px', fontSize: 16, fontWeight: 800, color: '#0f172a' },
  fieldLabel: { fontSize: 13, fontWeight: 700, color: '#475569', marginBottom: 8 },
  radioGroup: { display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 },
  radioRow: { display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' },
  radioInput: { width: 16, height: 16, cursor: 'pointer', accentColor: '#f59e0b' },
  radioText: { fontSize: 14, color: '#334155' },
  textarea: {
    width: '100%',
    padding: 10,
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    fontSize: 14,
    resize: 'vertical',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  },
  counter: { textAlign: 'right', fontSize: 12, color: '#94a3b8', margin: '4px 0 14px' },
  errorText: {
    color: '#dc2626',
    fontSize: 13,
    marginBottom: 12,
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: 8,
    padding: '10px 12px',
  },
  formActions: { display: 'flex', gap: 10 },
  cancelBtn: {
    padding: '10px 18px',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    background: '#fff',
    color: '#64748b',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },
  submitBtn: {
    padding: '10px 22px',
    borderRadius: 8,
    border: 'none',
    background: 'linear-gradient(135deg, #3b82f6, #10b981)',
    color: '#fff',
    fontSize: 14,
    fontWeight: 700,
    cursor: 'pointer',
  },
  terminalPanel: {
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    padding: 20,
  },
  pillBase: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 700,
  },
  pillPending: { background: '#fffbeb', border: '1px solid #fcd34d', color: '#b45309' },
  pillUpheld: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626' },
  pillDismissed: { background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#64748b' },
  terminalFiled: { fontSize: 13, color: '#94a3b8', margin: '12px 0 14px' },
  terminalRow: { display: 'flex', gap: 10, marginBottom: 12, fontSize: 14 },
  terminalLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: '#94a3b8',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  terminalValue: { fontSize: 14, color: '#0f172a', fontWeight: 600 },
  terminalDescBlock: { marginBottom: 4 },
  terminalDesc: {
    margin: '6px 0 0',
    fontSize: 14,
    color: '#334155',
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  terminalNotesBlock: {
    marginTop: 16,
    paddingTop: 14,
    borderTop: '1px solid #e2e8f0',
  },
};

export default DisputeForm;
